import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import {
  Autocomplete,
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Container,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import Copyright from './footer.js';
import FaqSection from './faqSection.js';
import HelpDialog from './helpDialog.js';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import {
  INDONESIA_ISO,
  buildCountryOptions,
  buildWhatsAppPhoneNumber,
  filterCountryOptions,
  formatCountryAriaLabel,
  formatCountryCodeLabel,
  getCountryByIso,
  isoToFlagEmoji,
  normalizeCountryCode,
  sanitizePhoneInput,
} from './phoneUtils.js';

const theme = createTheme();
const countryOptions = buildCountryOptions();

function NumberForm() {
  const [openHelper, setOpenHelper] = useState(false);
  const [formError, setFormError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(() =>
    getCountryByIso(INDONESIA_ISO, countryOptions)
  );
  const [localNumber, setLocalNumber] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    const countryCode = selectedCountry?.countryCodes[0] ?? '62';
    const result = buildWhatsAppPhoneNumber(localNumber, countryCode);

    if (!result.ok) {
      setFormError(true);
      setErrorMsg(result.error);
      return;
    }

    window.location.replace(
      `https://api.whatsapp.com/send?phone=${result.phone}`
    );
  };

  const handleNumberChange = (event) => {
    resetFormError();
    setLocalNumber(sanitizePhoneInput(event.target.value));
  };

  const handleCountryChange = (event, newCountry) => {
    resetFormError();
    if (newCountry) {
      setSelectedCountry(newCountry);
    }
  };

  const handleOpenHelper = () => {
    setOpenHelper(true);
  };

  const handleCloseHelper = () => {
    setOpenHelper(false);
  };

  const resetFormError = () => {
    setFormError(false);
    setErrorMsg('');
  };

  return (
    <ThemeProvider theme={theme}>
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          minHeight: '100vh',
        }}
      >
        <CssBaseline />
        <Box sx={{ mt: 4 }}>
          <Box
            component="img"
            sx={{
              width: 40,
              height: 40,
              m: 1,
            }}
            alt="Text Away — message WhatsApp without saving contacts"
            src="/resources/android-chrome-192x192.png"
          />
          <Typography component="h1" variant="h4">
            Text Away
          </Typography>
          <Typography component="h2" variant="subtitle1" sx={{ px: 1 }}>
            Chat on WhatsApp—no contact saved
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                mt: 2,
              }}
            >
              <Autocomplete
                id="country"
                sx={{
                  width: 140,
                  minWidth: 140,
                  flex: '0 0 140px',
                  '& .MuiOutlinedInput-root': {
                    flexWrap: 'nowrap',
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'nowrap',
                  },
                  '& .MuiAutocomplete-input': {
                    minWidth: 28,
                    width: 'auto !important',
                  },
                }}
                options={countryOptions}
                value={selectedCountry}
                onChange={handleCountryChange}
                getOptionLabel={formatCountryCodeLabel}
                filterOptions={filterCountryOptions}
                isOptionEqualToValue={(option, value) =>
                  option.isoCode2 === value.isoCode2
                }
                disableClearable
                autoHighlight
                componentsProps={{
                  paper: {
                    sx: { minWidth: 180 },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    label="Code"
                    placeholder="Search"
                    inputProps={{
                      ...params.inputProps,
                      'aria-label': selectedCountry
                        ? formatCountryAriaLabel(selectedCountry)
                        : 'Country code',
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {selectedCountry ? (
                            <Box
                              component="span"
                              aria-hidden
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                flexShrink: 0,
                                fontSize: '1.25rem',
                                lineHeight: 1,
                                mr: 0.5,
                              }}
                            >
                              {isoToFlagEmoji(selectedCountry.isoCode2)}
                            </Box>
                          ) : null}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, country) => {
                  const { key, ...optionProps } = props;
                  const code = normalizeCountryCode(country.countryCodes[0]);
                  return (
                    <Box
                      key={key}
                      component="li"
                      {...optionProps}
                      aria-label={formatCountryAriaLabel(country)}
                      sx={{
                        display: 'flex !important',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                        py: 1,
                      }}
                    >
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          display: 'inline-flex',
                          flexShrink: 0,
                          fontSize: '1.25rem',
                          lineHeight: 1,
                        }}
                      >
                        {isoToFlagEmoji(country.isoCode2)}
                      </Box>
                      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                        +{code}
                      </Box>
                    </Box>
                  );
                }}
              />
              <TextField
                onChange={handleNumberChange}
                margin="normal"
                sx={{ flex: 1 }}
                id="number"
                label="Phone number"
                name="number"
                value={localNumber}
                placeholder="0812 3456 7890"
                error={formError}
                helperText={formError ? errorMsg : undefined}
                autoFocus
              />
            </Box>
            <Button
              sx={{ mt: 1, mb: 2 }}
              type="submit"
              fullWidth
              variant="contained"
            >
              Open in WhatsApp
            </Button>
            <Button variant="text" onClick={handleOpenHelper}>
              Help ?
            </Button>
            <HelpDialog open={openHelper} onClose={handleCloseHelper} />
          </Box>
          <FaqSection />
        </Box>
        <Box sx={{ height: 200, mt: 'auto' }}>
          <Copyright />
        </Box>
      </Container>
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<NumberForm />);

serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    alert('New version available! Please update now.');
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  },
});
